import type {
  TabsPanelProps,
  TabsProps,
  TabsTabProps} from '@mantine/core';
import {Tabs} from '@mantine/core';

export type ExtensionView = `ext-${string}`;

export interface IUrlQueryTabConfig<ViewType extends string> {
  view: ViewType;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
  component: React.ReactNode;
  tabsTabProps?: TabsTabProps;
  tabsPanelProps?: TabsPanelProps;
}

export interface useUrlQueryTabPropsV2<ViewType extends string> {
  view?: ViewType;
  defaultView: ViewType;
  preloadTabs?: boolean;
  tabs: IUrlQueryTabConfig<ViewType | ExtensionView>[];
  onSelectView: (view: string) => void;
  tabsProps?: TabsProps;
}

export const useUrlQueryTabV2 = <ViewType extends string>({
  view,
  defaultView,
  preloadTabs = false,
  tabs,
  onSelectView,
  tabsProps,
}: useUrlQueryTabPropsV2<ViewType>) => {
  const defaultTab = tabs?.find(tab => tab.view === defaultView);
  const matches = tabs?.filter(tab => tab.view === view);

  let selectedTab = defaultTab ? defaultTab.view : tabs[0].view;
  if (matches.length === 1) {
    selectedTab = matches[0].view;
  }

  const handleTabChange = (value: string | null) => {
    const match = tabs?.find(tab => tab.view === value);
    if (match) {
      onSelectView(match.view);
    }
  };

  const RenderedTabsComponent = (
    <Tabs
      value={selectedTab}
      onChange={handleTabChange}
      keepMounted={preloadTabs}
      {...tabsProps}>
      <Tabs.List>
        {tabs
          .filter(i => i.hidden != true)
          .map(i => (
            <Tabs.Tab
              key={i.view}
              value={i.view}
              disabled={i.disabled}
              {...i.tabsTabProps}>
              {i.label}
            </Tabs.Tab>
          ))}
      </Tabs.List>

      {tabs.map(i => (
        <Tabs.Panel key={i.view} value={i.view} pt="md" {...i.tabsPanelProps}>
          {i.component}
        </Tabs.Panel>
      ))}
    </Tabs>
  );

  return {
    selectedTab,
    handleTabChange,
    RenderedTabsComponent,
  };
};
